import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { warmNavigationTarget } from "../services/prefetch";
import { useCoachAuth } from "../context/CoachAuthContext";
import "./Sidebar.css";

const cx = (...tokens) => tokens.filter(Boolean).join(" ");

const iconProps = {
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.9,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
};

const DashboardIcon = () => (
  <svg {...iconProps}>
    <path d="M3 13h8V3H3zM13 21h8v-6h-8zM13 10h8V3h-8zM3 21h8v-4H3z" />
  </svg>
);

const ClientsIcon = () => (
  <svg {...iconProps}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
    <circle cx="9.5" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const AdaptationsIcon = () => (
  <svg {...iconProps}>
    <path d="M4 18V9M10 18V5M16 18v-7M22 18V3" />
    <path d="m3 13 6-4 6 2 7-6" />
  </svg>
);

const GroupsIcon = () => (
  <svg {...iconProps}>
    <rect x="3" y="4" width="8" height="7" rx="2" />
    <rect x="13" y="4" width="8" height="7" rx="2" />
    <rect x="8" y="13" width="8" height="7" rx="2" />
    <path d="M12 11v2" />
  </svg>
);

const MessagesIcon = () => (
  <svg {...iconProps}>
    <path d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5c-1.4 0-2.71-.34-3.86-.94L3 21l1.94-5.64A8.47 8.47 0 0 1 4 11.5 8.5 8.5 0 1 1 21 11.5Z" />
  </svg>
);

const FeedbackIcon = () => (
  <svg {...iconProps}>
    <path d="M12 17h.01" />
    <path d="M9.09 9a3 3 0 1 1 5.82 1c0 2-3 3-3 3" />
    <circle cx="12" cy="12" r="9" />
  </svg>
);

const TestsIcon = () => (
  <svg {...iconProps}>
    <path d="M9 3h6" />
    <path d="M10 9 4.5 3.5" />
    <path d="m14 9 5.5-5.5" />
    <path d="M8 21h8" />
    <path d="M12 9v12" />
  </svg>
);

const ProgramIcon = () => (
  <svg {...iconProps}>
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <path d="M8 9h8M8 13h8M8 17h5" />
  </svg>
);

const ExerciseIcon = () => (
  <svg {...iconProps}>
    <path d="M6.5 6.5 9 9m6 6 2.5 2.5M3 10l7-7m4 18 7-7" />
    <path d="m6 13 5-5m2 8 5-5" />
  </svg>
);

const SessionIcon = () => (
  <svg {...iconProps}>
    <rect x="3" y="4" width="18" height="17" rx="2" />
    <path d="M8 2v4M16 2v4M3 10h18" />
  </svg>
);

const DiagramIcon = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="12" r="3" />
    <path d="M3 12h6M15 12h6M12 3v6M12 15v6" />
    <path d="M5.6 5.6l4.2 4.2M14.2 14.2l4.2 4.2M18.4 5.6l-4.2 4.2M9.8 14.2l-4.2 4.2" />
  </svg>
);

const CalendarLaunchIcon = () => (
  <svg {...iconProps}>
    <rect x="3" y="4" width="18" height="17" rx="2" />
    <path d="M8 2v4M16 2v4M3 10h18" />
    <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" />
  </svg>
);

const SettingsIcon = () => (
  <svg {...iconProps}>
    <path d="M12 2v3M12 19v3M4.93 4.93l2.12 2.12M16.95 16.95l2.12 2.12M2 12h3M19 12h3M4.93 19.07l2.12-2.12M16.95 7.05l2.12-2.12" />
    <circle cx="12" cy="12" r="4" />
  </svg>
);

const LogoutIcon = () => (
  <svg {...iconProps}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="m16 17 5-5-5-5" />
    <path d="M21 12H9" />
  </svg>
);

function NavLabel({ icon: Icon, label, sub = false }) {
  return (
    <span className={sub ? "sidebar-submenu__content" : "sidebar-link__content"}>
      <span className={sub ? "sidebar-submenu__icon" : "sidebar-link__icon"}>
        <Icon />
      </span>
      <span>{label}</span>
    </span>
  );
}

export default function Sidebar({ open = false, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { coach, isAdmin, hasFeature, logout } = useCoachAuth();
  const [programOpen, setProgramOpen] = useState(false);

  const isProgramsSection = useMemo(
    () =>
      location.pathname.startsWith("/programs") ||
      location.pathname.startsWith("/sessions") ||
      location.pathname.startsWith("/exercises") ||
      location.pathname.startsWith("/tests") ||
      location.pathname.startsWith("/diagrams"),
    [location.pathname]
  );

  useEffect(() => {
    if (!isProgramsSection) setProgramOpen(false);
  }, [isProgramsSection]);

  const getPrefetchProps = (path) => ({
    onMouseEnter: () => warmNavigationTarget(path),
    onFocus: () => warmNavigationTarget(path),
  });

  const handleItemClick = () => {
    if (typeof window !== "undefined" && window.innerWidth <= 1024) {
      onClose?.();
    }
  };

  return (
    <aside className={cx("sidebar-panel", open && "is-open")}>
      <div className="sidebar-panel__header">
        <div>
          <div className="sidebar-panel__eyebrow">Oval CRM</div>
          <h2 className="sidebar-panel__logo">Coach</h2>
          <div className="sidebar-panel__identity">
            {coach?.first_name || coach?.email || "Coach"}
          </div>
        </div>
        <button
          type="button"
          className="sidebar-panel__close"
          onClick={onClose}
          aria-label="Fermer le menu"
        >
          ×
        </button>
      </div>

      <nav className="sidebar-nav" aria-label="Navigation principale">
        {hasFeature("dashboard") ? (
          <Link
            to="/"
            {...getPrefetchProps("/")}
            onClick={handleItemClick}
            className={cx("sidebar-link", location.pathname === "/" && "is-active")}
            aria-current={location.pathname === "/" ? "page" : undefined}
          >
            <NavLabel icon={DashboardIcon} label="Tableau de bord" />
          </Link>
        ) : null}
        {hasFeature("clients") ? (
          <Link
            to="/clients"
            {...getPrefetchProps("/clients")}
            onClick={handleItemClick}
            className={cx(
              "sidebar-link",
              location.pathname.startsWith("/clients") && "is-active"
            )}
            aria-current={location.pathname.startsWith("/clients") ? "page" : undefined}
          >
            <NavLabel icon={ClientsIcon} label="Clients" />
          </Link>
        ) : null}
        {hasFeature("clients") ? (
          <Link
            to="/adaptations"
            {...getPrefetchProps("/adaptations")}
            onClick={handleItemClick}
            className={cx(
              "sidebar-link",
              location.pathname.startsWith("/adaptations") && "is-active"
            )}
            aria-current={location.pathname.startsWith("/adaptations") ? "page" : undefined}
          >
            <NavLabel icon={AdaptationsIcon} label="Adaptations" />
          </Link>
        ) : null}
        {hasFeature("groups") ? (
          <Link
            to="/groups"
            {...getPrefetchProps("/groups")}
            onClick={handleItemClick}
            className={cx(
              "sidebar-link",
              location.pathname.startsWith("/groups") && "is-active"
            )}
            aria-current={location.pathname.startsWith("/groups") ? "page" : undefined}
          >
            <NavLabel icon={GroupsIcon} label="Groupes" />
          </Link>
        ) : null}
        {hasFeature("programs") ? (
          <button
            type="button"
            onClick={() => {
              setProgramOpen(true);
              navigate("/programs?tab=builder");
              handleItemClick();
            }}
            onMouseEnter={() => warmNavigationTarget("/programs")}
            onFocus={() => warmNavigationTarget("/programs")}
            className={cx(
              "sidebar-link",
              "sidebar-link--button",
              isProgramsSection && "is-active"
            )}
            aria-expanded={programOpen}
          >
            <NavLabel icon={ProgramIcon} label="Contenu" />
          </button>
        ) : null}

        {hasFeature("programs") && programOpen && (
          <div className="sidebar-submenu">
            <Link
              to="/sessions"
              {...getPrefetchProps("/sessions")}
              onClick={handleItemClick}
              className={cx(
                "sidebar-submenu__link",
                location.pathname.startsWith("/sessions") && "is-active"
              )}
              aria-current={location.pathname.startsWith("/sessions") ? "page" : undefined}
            >
              <NavLabel icon={SessionIcon} label="Séances" sub />
            </Link>
            <Link
              to="/programs?tab=builder"
              {...getPrefetchProps("/programs")}
              onClick={handleItemClick}
              className={cx(
                "sidebar-submenu__link",
                location.pathname.startsWith("/programs") && "is-active"
              )}
              aria-current={location.pathname.startsWith("/programs") ? "page" : undefined}
            >
              <NavLabel icon={ProgramIcon} label="Programmes" sub />
            </Link>
            <Link
              to="/exercises"
              {...getPrefetchProps("/exercises")}
              onClick={handleItemClick}
              className={cx(
                "sidebar-submenu__link",
                location.pathname === "/exercises" && "is-active"
              )}
              aria-current={location.pathname === "/exercises" ? "page" : undefined}
            >
              <NavLabel icon={ExerciseIcon} label="Exercices" sub />
            </Link>
            {hasFeature("tests") && (
              <Link
                to="/tests"
                {...getPrefetchProps("/tests")}
                onClick={handleItemClick}
                className={cx(
                  "sidebar-submenu__link",
                  location.pathname.startsWith("/tests") && "is-active"
                )}
                aria-current={location.pathname.startsWith("/tests") ? "page" : undefined}
              >
                <NavLabel icon={TestsIcon} label="Tests physiques" sub />
              </Link>
            )}
            <Link
              to="/diagrams"
              {...getPrefetchProps("/diagrams")}
              onClick={handleItemClick}
              className={cx(
                "sidebar-submenu__link",
                location.pathname.startsWith("/diagrams") && "is-active"
              )}
              aria-current={location.pathname.startsWith("/diagrams") ? "page" : undefined}
            >
              <NavLabel icon={DiagramIcon} label="Schémas terrain" sub />
            </Link>
          </div>
        )}

        {hasFeature("messages") ? (
          <Link
            to="/messages"
            {...getPrefetchProps("/messages")}
            onClick={handleItemClick}
            className={cx(
              "sidebar-link",
              location.pathname.startsWith("/messages") && "is-active"
            )}
            aria-current={location.pathname.startsWith("/messages") ? "page" : undefined}
          >
            <NavLabel icon={MessagesIcon} label="Messagerie" />
          </Link>
        ) : null}
        {isAdmin ? (
          <Link
            to="/launch-calendar"
            {...getPrefetchProps("/launch-calendar")}
            onClick={handleItemClick}
            className={cx(
              "sidebar-link",
              location.pathname.startsWith("/launch-calendar") && "is-active"
            )}
            aria-current={location.pathname.startsWith("/launch-calendar") ? "page" : undefined}
          >
            <NavLabel icon={CalendarLaunchIcon} label="Lancement" />
          </Link>
        ) : null}
        {isAdmin ? (
          <Link
            to="/feedback"
            {...getPrefetchProps("/feedback")}
            onClick={handleItemClick}
            className={cx(
              "sidebar-link",
              location.pathname.startsWith("/feedback") && "is-active"
            )}
            aria-current={location.pathname.startsWith("/feedback") ? "page" : undefined}
          >
            <NavLabel icon={FeedbackIcon} label="Retours produit" />
          </Link>
        ) : null}

        <Link
          to="/settings"
          {...getPrefetchProps("/settings")}
          onClick={handleItemClick}
          className={cx(
            "sidebar-link",
            location.pathname.startsWith("/settings") && "is-active"
          )}
          aria-current={location.pathname.startsWith("/settings") ? "page" : undefined}
        >
          <NavLabel icon={SettingsIcon} label="Paramètres" />
        </Link>
      </nav>

      <button
        type="button"
        className="sidebar-link sidebar-link--button sidebar-link--logout"
        onClick={() => {
          logout();
          handleItemClick();
          navigate("/login", { replace: true });
        }}
      >
        <NavLabel icon={LogoutIcon} label="Se déconnecter" />
      </button>
    </aside>
  );
}
